import React, { useState, useEffect, useCallback, useMemo } from 'react';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import './TableroGerencial.css';

const MESES_CORTO = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatCurrency = (val) => {
  if (!val || isNaN(val)) return '-';
  if (Math.abs(val) >= 1000000000) return '$ ' + (val / 1000000000).toFixed(1) + 'B';
  if (Math.abs(val) >= 1000000) return '$ ' + (val / 1000000).toFixed(0) + 'M';
  return '$ ' + Math.round(val).toLocaleString('es-CO');
};

const formatCurrencyFull = (val) => {
  if (!val || isNaN(val)) return '-';
  return '$ ' + Math.round(val).toLocaleString('es-CO');
};

const formatPct = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '-';
  return (val * 100).toFixed(1) + '%';
};

export default function TableroGerencial() {
  const { error } = useNotification();
  const [anio] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const d = await client.query(`query($anio: Int) { gerenciaTablero(anio: $anio) }`, { anio });
      if (d.gerenciaTablero) setData({ success: true, ...d.gerenciaTablero });
    } catch (err) { error('Error cargando tablero'); }
    finally { setLoading(false); }
  }, [anio, error]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Datos procesados
  const cards = useMemo(() => {
    if (!data) return null;
    const c = data.comercial?.totales;
    const m = data.marketing?.totales;
    const t = data.tecnico;
    return { comercial: c, marketing: m, tecnico: t };
  }, [data]);

  if (loading) {
    return <div className="tg-loading"><div className="spinner"></div><p>Cargando Tablero Gerencial...</p></div>;
  }

  if (!data || !cards) {
    return <div className="tg-empty"><p>No hay datos disponibles</p></div>;
  }

  const { comercial, marketing, tecnico } = cards;
  const mesesComercial = data.comercial?.meses || [];
  const mesesMarketing = data.marketing?.meses || [];

  // Semáforo helper
  const semaforo = (val, umbralVerde = 1, umbralAmarillo = 0.7) => {
    if (val === null || val === undefined || isNaN(val)) return 'gris';
    if (val >= umbralVerde) return 'verde';
    if (val >= umbralAmarillo) return 'amarillo';
    return 'rojo';
  };

  return (
    <div className="tablero-gerencial">
      {/* Header */}
      <div className="tg-header">
        <div>
          <h1>Tablero Gerencial</h1>
          <p>Vista ejecutiva consolidada — {anio}</p>
        </div>
        <button className="tg-btn-refresh" onClick={fetchData}>Actualizar</button>
      </div>

      {/* ============ CARDS PRINCIPALES ============ */}
      <div className="tg-cards-grid">
        {/* Comercial */}
        <div className="tg-main-card comercial"
          onMouseEnter={(e) => setTooltip({ type: 'comercial', x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setTooltip(null)}>
          <div className="tg-card-icon">📊</div>
          <div className="tg-card-body">
            <span className="tg-card-label">Ventas del Año</span>
            <span className="tg-card-value">{comercial?.unidades_real || 0} <small>unidades</small></span>
            <span className="tg-card-sub">{formatCurrencyFull(comercial?.ventas_real)}</span>
          </div>
          <div className={`tg-card-indicator ${semaforo(comercial?.cumpl_ventas)}`}>
            {formatPct(comercial?.cumpl_ventas)}
          </div>
        </div>

        <div className="tg-main-card cumplimiento"
          onMouseEnter={(e) => setTooltip({ type: 'cumplimiento', x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setTooltip(null)}>
          <div className="tg-card-icon">🎯</div>
          <div className="tg-card-body">
            <span className="tg-card-label">Cumplimiento Meta</span>
            <span className="tg-card-value">{formatPct(comercial?.cumpl_unidades)}</span>
            <span className="tg-card-sub">{comercial?.unidades_real || 0} de {comercial?.unidades_meta || 0} unidades</span>
          </div>
          <div className={`tg-card-indicator ${semaforo(comercial?.cumpl_unidades)}`}>
            {comercial?.cumpl_unidades >= 1 ? '✓' : '↗'}
          </div>
        </div>

        <div className="tg-main-card marketing"
          onMouseEnter={(e) => setTooltip({ type: 'marketing', x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setTooltip(null)}>
          <div className="tg-card-icon">📈</div>
          <div className="tg-card-body">
            <span className="tg-card-label">ROAS Promedio</span>
            <span className="tg-card-value">{marketing?.roas_promedio || 0}x</span>
            <span className="tg-card-sub">CAC: {formatCurrency(marketing?.cac_promedio)}</span>
          </div>
          <div className={`tg-card-indicator ${semaforo(marketing?.roas_promedio, 50, 20)}`}>
            {marketing?.ventas_mkt || 0} ventas
          </div>
        </div>

        <div className="tg-main-card tecnico"
          onMouseEnter={(e) => setTooltip({ type: 'tecnico', x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setTooltip(null)}>
          <div className="tg-card-icon">🏗️</div>
          <div className="tg-card-body">
            <span className="tg-card-label">Proyectos Activos</span>
            <span className="tg-card-value">{tecnico?.proyectos_activos || 0}</span>
            <span className="tg-card-sub">{tecnico?.finalizados || 0} finalizados</span>
          </div>
          <div className={`tg-card-indicator verde`}>
            {tecnico?.a_tiempo || 0} a tiempo
          </div>
        </div>

        <div className="tg-main-card margen"
          onMouseEnter={(e) => setTooltip({ type: 'margen', x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setTooltip(null)}>
          <div className="tg-card-icon">💰</div>
          <div className="tg-card-body">
            <span className="tg-card-label">Margen Neto</span>
            <span className="tg-card-value">{formatCurrency(comercial?.neto)}</span>
            <span className="tg-card-sub">Margen: {formatCurrency(comercial?.margen_real)} - Costos: {formatCurrency(comercial?.costos)}</span>
          </div>
          <div className={`tg-card-indicator ${(comercial?.neto || 0) >= 0 ? 'verde' : 'rojo'}`}>
            {(comercial?.neto || 0) >= 0 ? '↑' : '↓'}
          </div>
        </div>

        <div className="tg-main-card utilidad">
          <div className="tg-card-icon">📐</div>
          <div className="tg-card-body">
            <span className="tg-card-label">Utilidad Promedio</span>
            <span className="tg-card-value">{formatPct(tecnico?.utilidad_promedio)}</span>
            <span className="tg-card-sub">Esperada: 25%</span>
          </div>
          <div className={`tg-card-indicator ${semaforo(tecnico?.utilidad_promedio, 0.25, 0.15)}`}>
            {tecnico?.utilidad_promedio >= 0.25 ? '✓' : '↗'}
          </div>
        </div>
      </div>

      {/* ============ GRÁFICA VENTAS vs META ============ */}
      <div className="tg-section">
        <h2>Ventas Reales vs Meta por Mes</h2>
        <div className="tg-chart-bars">
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(mes => {
            const mc = mesesComercial.find(m => m.mes === mes);
            const meta = mc?.ventas_meta || 0;
            const real = mc?.ventas_real || 0;
            const maxVal = Math.max(...mesesComercial.map(m => Math.max(m.ventas_meta || 0, m.ventas_real || 0)), 1);
            const hMeta = meta > 0 ? (meta / maxVal) * 160 : 0;
            const hReal = real > 0 ? (real / maxVal) * 160 : 0;
            const cumpl = meta > 0 ? real / meta : 0;

            return (
              <div key={mes} className="tg-bar-group"
                onMouseEnter={(e) => setTooltip({ type: 'bar', mes, meta, real, cumpl, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTooltip(null)}>
                <div className="tg-bars">
                  <div className="tg-bar meta" style={{ height: hMeta + 'px' }}></div>
                  <div className={`tg-bar real ${semaforo(cumpl)}`} style={{ height: hReal + 'px' }}></div>
                </div>
                <span className="tg-bar-label">{MESES_CORTO[mes]}</span>
              </div>
            );
          })}
        </div>
        <div className="tg-bar-legend">
          <span><span className="tg-legend-box meta"></span> Meta</span>
          <span><span className="tg-legend-box verde"></span> Real (cumplido)</span>
          <span><span className="tg-legend-box rojo"></span> Real (por debajo)</span>
        </div>
      </div>

      {/* ============ SEMÁFOROS POR MÓDULO ============ */}
      <div className="tg-semaforos-grid">
        {/* Comercial */}
        <div className="tg-semaforo-card">
          <h3>📊 Comercial</h3>
          <div className="tg-semaforo-items">
            <div className="tg-sem-item">
              <span className="tg-sem-label">Unidades</span>
              <span className={`tg-sem-dot ${semaforo(comercial?.cumpl_unidades)}`}></span>
              <span className="tg-sem-value">{formatPct(comercial?.cumpl_unidades)}</span>
            </div>
            <div className="tg-sem-item">
              <span className="tg-sem-label">Ventas $</span>
              <span className={`tg-sem-dot ${semaforo(comercial?.cumpl_ventas)}`}></span>
              <span className="tg-sem-value">{formatPct(comercial?.cumpl_ventas)}</span>
            </div>
            <div className="tg-sem-item">
              <span className="tg-sem-label">Neto</span>
              <span className={`tg-sem-dot ${(comercial?.neto || 0) >= 0 ? 'verde' : 'rojo'}`}></span>
              <span className="tg-sem-value">{formatCurrency(comercial?.neto)}</span>
            </div>
          </div>
        </div>

        {/* Marketing */}
        <div className="tg-semaforo-card">
          <h3>📈 Marketing</h3>
          <div className="tg-semaforo-items">
            <div className="tg-sem-item">
              <span className="tg-sem-label">ROAS</span>
              <span className={`tg-sem-dot ${semaforo(marketing?.roas_promedio, 50, 20)}`}></span>
              <span className="tg-sem-value">{marketing?.roas_promedio || 0}x</span>
            </div>
            <div className="tg-sem-item">
              <span className="tg-sem-label">CAC</span>
              <span className={`tg-sem-dot ${(marketing?.cac_promedio || 0) < 1000000 ? 'verde' : 'amarillo'}`}></span>
              <span className="tg-sem-value">{formatCurrency(marketing?.cac_promedio)}</span>
            </div>
            <div className="tg-sem-item">
              <span className="tg-sem-label">Leads</span>
              <span className={`tg-sem-dot ${(marketing?.leads || 0) > 0 ? 'verde' : 'gris'}`}></span>
              <span className="tg-sem-value">{marketing?.leads || 0}</span>
            </div>
          </div>
        </div>

        {/* Técnico */}
        <div className="tg-semaforo-card">
          <h3>🏗️ Técnico</h3>
          <div className="tg-semaforo-items">
            <div className="tg-sem-item">
              <span className="tg-sem-label">Activos</span>
              <span className={`tg-sem-dot verde`}></span>
              <span className="tg-sem-value">{tecnico?.proyectos_activos || 0}</span>
            </div>
            <div className="tg-sem-item">
              <span className="tg-sem-label">A Tiempo</span>
              <span className={`tg-sem-dot verde`}></span>
              <span className="tg-sem-value">{tecnico?.a_tiempo || 0}</span>
            </div>
            <div className="tg-sem-item">
              <span className="tg-sem-label">Utilidad</span>
              <span className={`tg-sem-dot ${semaforo(tecnico?.utilidad_promedio, 0.25, 0.15)}`}></span>
              <span className="tg-sem-value">{formatPct(tecnico?.utilidad_promedio)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============ MARKETING MENSUAL ============ */}
      <div className="tg-section">
        <h2>Marketing — Tendencia Mensual</h2>
        <div className="tg-mkt-grid">
          {mesesMarketing.filter(m => m.ventas_mkt > 0).map(m => (
            <div key={m.mes} className="tg-mkt-card"
              onMouseEnter={(e) => setTooltip({ type: 'mkt', data: m, x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setTooltip(null)}>
              <span className="tg-mkt-mes">{MESES_CORTO[m.mes]}</span>
              <span className="tg-mkt-ventas">{m.ventas_mkt} ventas</span>
              <span className="tg-mkt-dinero">{formatCurrency(m.dinero_mkt)}</span>
              <span className="tg-mkt-roas">ROAS: {m.roas}x</span>
            </div>
          ))}
        </div>
      </div>

      {/* ============ PROYECTOS TÉCNICO ============ */}
      {tecnico?.proyectos?.length > 0 && (
        <div className="tg-section">
          <h2>Proyectos — Utilidad</h2>
          <div className="tg-proyectos-grid">
            {tecnico.proyectos.sort((a, b) => b.utilidad_pct - a.utilidad_pct).map((p, i) => (
              <div key={i} className="tg-proy-card"
                onMouseEnter={(e) => setTooltip({ type: 'proy', data: p, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTooltip(null)}>
                <div className="tg-proy-header">
                  <strong>{p.proyecto}</strong>
                  <span className="tg-proy-apto">{p.apto}</span>
                </div>
                <div className="tg-proy-bar-container">
                  <div className={`tg-proy-bar ${p.utilidad_pct >= 0.25 ? 'verde' : p.utilidad_pct >= 0.15 ? 'amarillo' : 'rojo'}`}
                    style={{ width: Math.max(5, Math.min(100, p.utilidad_pct * 100 * 2)) + '%' }}>
                  </div>
                  <span className="tg-proy-pct">{formatPct(p.utilidad_pct)}</span>
                </div>
                <div className="tg-proy-vals">
                  <span>Ppto: {formatCurrency(p.presupuesto)}</span>
                  <span>Costo: {formatCurrency(p.costo_real)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ TOOLTIP GLOBAL ============ */}
      {tooltip && (
        <div className="tg-tooltip" style={{ left: Math.min(tooltip.x + 12, window.innerWidth - 280), top: tooltip.y - 10 }}>
          {tooltip.type === 'comercial' && (
            <>
              <div className="tg-tooltip-title">Seguimiento Comercial {anio}</div>
              <div className="tg-tooltip-row">Meta unidades: <strong>{comercial?.unidades_meta}</strong></div>
              <div className="tg-tooltip-row">Real unidades: <strong>{comercial?.unidades_real}</strong></div>
              <div className="tg-tooltip-row">Meta ventas: <strong>{formatCurrencyFull(comercial?.ventas_meta)}</strong></div>
              <div className="tg-tooltip-row">Real ventas: <strong>{formatCurrencyFull(comercial?.ventas_real)}</strong></div>
            </>
          )}
          {tooltip.type === 'cumplimiento' && (
            <>
              <div className="tg-tooltip-title">Cumplimiento de Meta</div>
              <div className="tg-tooltip-row">Unidades: <strong>{formatPct(comercial?.cumpl_unidades)}</strong></div>
              <div className="tg-tooltip-row">Ventas $: <strong>{formatPct(comercial?.cumpl_ventas)}</strong></div>
              <div className="tg-tooltip-row">{comercial?.unidades_real} de {comercial?.unidades_meta} unidades vendidas</div>
            </>
          )}
          {tooltip.type === 'marketing' && (
            <>
              <div className="tg-tooltip-title">Marketing {anio}</div>
              <div className="tg-tooltip-row">Ventas Mkt: <strong>{marketing?.ventas_mkt} unidades</strong></div>
              <div className="tg-tooltip-row">Dinero: <strong>{formatCurrencyFull(marketing?.dinero_mkt)}</strong></div>
              <div className="tg-tooltip-row">Leads totales: <strong>{marketing?.leads}</strong></div>
              <div className="tg-tooltip-row">Inversión: <strong>{formatCurrencyFull(marketing?.inversion)}</strong></div>
              <div className="tg-tooltip-row">ROAS: <strong>{marketing?.roas_promedio}x</strong></div>
              <div className="tg-tooltip-row">CAC: <strong>{formatCurrencyFull(marketing?.cac_promedio)}</strong></div>
            </>
          )}
          {tooltip.type === 'tecnico' && (
            <>
              <div className="tg-tooltip-title">KPI Técnico</div>
              <div className="tg-tooltip-row">Activos: <strong>{tecnico?.proyectos_activos}</strong></div>
              <div className="tg-tooltip-row">Finalizados: <strong>{tecnico?.finalizados}</strong></div>
              <div className="tg-tooltip-row">A tiempo: <strong>{tecnico?.a_tiempo}</strong></div>
              <div className="tg-tooltip-row">Utilidad prom: <strong>{formatPct(tecnico?.utilidad_promedio)}</strong></div>
            </>
          )}
          {tooltip.type === 'margen' && (
            <>
              <div className="tg-tooltip-title">Margen y Rentabilidad</div>
              <div className="tg-tooltip-row">Ventas: <strong>{formatCurrencyFull(comercial?.ventas_real)}</strong></div>
              <div className="tg-tooltip-row">Margen: <strong>{formatCurrencyFull(comercial?.margen_real)}</strong></div>
              <div className="tg-tooltip-row">Costos/Gastos: <strong>{formatCurrencyFull(comercial?.costos)}</strong></div>
              <div className="tg-tooltip-row">Neto: <strong>{formatCurrencyFull(comercial?.neto)}</strong></div>
            </>
          )}
          {tooltip.type === 'bar' && (
            <>
              <div className="tg-tooltip-title">{MESES_CORTO[tooltip.mes]} {anio}</div>
              <div className="tg-tooltip-row">Meta: <strong>{formatCurrencyFull(tooltip.meta)}</strong></div>
              <div className="tg-tooltip-row">Real: <strong>{formatCurrencyFull(tooltip.real)}</strong></div>
              <div className="tg-tooltip-row">Cumplimiento: <strong>{formatPct(tooltip.cumpl)}</strong></div>
            </>
          )}
          {tooltip.type === 'mkt' && (
            <>
              <div className="tg-tooltip-title">Marketing — {MESES_CORTO[tooltip.data.mes]}</div>
              <div className="tg-tooltip-row">Ventas: <strong>{tooltip.data.ventas_mkt} unidades</strong></div>
              <div className="tg-tooltip-row">Dinero: <strong>{formatCurrencyFull(tooltip.data.dinero_mkt)}</strong></div>
              <div className="tg-tooltip-row">Leads: <strong>{tooltip.data.leads}</strong></div>
              <div className="tg-tooltip-row">Inversión: <strong>{formatCurrencyFull(tooltip.data.inversion)}</strong></div>
              <div className="tg-tooltip-row">ROAS: <strong>{tooltip.data.roas}x</strong></div>
              <div className="tg-tooltip-row">CAC: <strong>{formatCurrencyFull(tooltip.data.cac)}</strong></div>
            </>
          )}
          {tooltip.type === 'proy' && (
            <>
              <div className="tg-tooltip-title">{tooltip.data.proyecto} — {tooltip.data.apto}</div>
              <div className="tg-tooltip-row">Presupuesto: <strong>{formatCurrencyFull(tooltip.data.presupuesto)}</strong></div>
              <div className="tg-tooltip-row">Costo real: <strong>{formatCurrencyFull(tooltip.data.costo_real)}</strong></div>
              <div className="tg-tooltip-row">Utilidad: <strong>{formatPct(tooltip.data.utilidad_pct)}</strong></div>
              <div className="tg-tooltip-row">Diferencia: <strong>{formatCurrencyFull(tooltip.data.presupuesto - tooltip.data.costo_real)}</strong></div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
